import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../AppContext";
import TitleBar from '../components/titleBar';
import { Box, IconButton, Typography, Link, MenuItem, Button, Paper, Popover, Checkbox, FormControlLabel, Switch, Radio, RadioGroup, Slider } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

import Service from "../services/service";
import * as actions from "../actions/actions";
import Table from '../components/table';
import { clickDownloadLink, config, timeConverter, resolveNotificationMessage, formatMoney } from '../helpers/constants';
import LoadingScreen from '../components/LoadingScreen.js';
import Pagination from '../components/pagination';
import IconLabel from '../components/IconLabel';
import DividerLabel from "../components/DividerLabel";
import SimpleTable from "../components/SimpleTable";
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { Clear, Equalizer } from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import DashboardChart from "../components/DashboardChart";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    bodyContainer: {
        display: 'flex',
        flex: "1 1 0",
        minHeight: 0
    },
    tableContainer: {
        flex: "1 1",
        overflow: "hidden",
        overflowY: "auto",
        height: 0,
    },
    detailsContainer: {
        flex: "1 1",
        overflow: "hidden",
        overflowY: "auto",
        height: 0
    },
    bottom: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 'auto',
        padding: '10px 10px 10px 15px',
        height: '43px',
        borderTop: "1px solid #dddddd",
    },
    pagination: {
        marginLeft: 'auto'
    },
    paper: {
        position: "relative",
        width: "50%",
        display: "flex",
        flex: "1 1",
        margin: 10,
        boxShadow: "0px 0px 2px 2px rgba(0, 0, 0, 0.1)",
        zIndex: 2,
        flexDirection: 'column',
        minHeight: 0
    },
    detailsPanel: {
        backgroundColor: '#ffffff',
        width: "50%",
        minWidth: 350,
        padding: 16,
        minHeight: 0,
        overflowY: "auto"
    },

    table_status_header: {
        width: '50px',
        textAlign: 'center'
    },
    table_license_header: {
        width: "150px",
    },
    table_mail_header: {
        width: '50%',
        minWidth: "100px",
    },
    table_credits_header: {
        width: '60px',
        textAlign: "right",
        padding: 8
    },
    table_action_header: {
        width: '60px',
        textAlign: 'center'

    },
    textarea: {
        background: "transparent",
        resize: "none",
        border: "1px solid #AAAAAA",
        width: "100%",
        outline: "none",
        height: "100%",  // textarea rounds up to next line height, so make sure not to display scroll bars if empty
        fontFamily: "inherit",
        fontSize: "inherit",
    },
}))

export default function LicenseComponent(props) {
    const state = useContext(AppContext);
    const s = state.strings;
    const classes = useStyles(state);
    const dispatch = useContext(DispatchContext);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [welcomeMessage, setWelcomeMessage] = useState("");
    const [editMarkdown, setEditMarkdown] = useState(false);
    const [chartValue, setChartValue] = useState("credits");
    const license = state.licenseAdmin.currentLicense;
    const termsLabel = license.terms && (license.terms.includes("month") ? s.licenseAdmin.months.format(license.terms.substring(0, 1)) : s.licenseAdmin.years.format(license.terms.substring(0, 1)));
    const noRenewPeriod = Boolean(((Date.now() / 1000) - license.validFrom) < 1 * 24 * 60 * 60);

    const [exportFormatPopup, setExportFormatPopup] = React.useState(null);
    const [perUserQuota, setPerUserQuota] = React.useState(0);

    const chartModes = {
        reports: {
            value: "reports",
            label: s.licenseAdmin.chartReports,
            datasets: JSON.parse(JSON.stringify(state.licenseAdmin.chartData.sets.reports ?? "")),    // Chart.js extends this array, so we need to deep copy
            title: s.licenseAdmin.chartReports + " " + timeConverter(state.licenseAdmin.chartData.from, null, s.common.dateFormat) + " - " + timeConverter(state.licenseAdmin.chartData.to, null, s.common.dateFormat)
        },
        credits: {
            value: "credits",
            label: s.licenseAdmin.chartCredits,
            datasets: JSON.parse(JSON.stringify(state.licenseAdmin.chartData.sets.credits ?? "")), // Chart.js extends this array, so we need to deep copy
            title: s.licenseAdmin.chartCredits + " " + timeConverter(state.licenseAdmin.chartData.from, null, s.common.dateFormat) + " - " + timeConverter(state.licenseAdmin.chartData.to, null, s.common.dateFormat)
        },
        reportsagg: {
            value: "reportsagg",
            label: s.licenseAdmin.chartSumReports,
            datasets: JSON.parse(JSON.stringify(state.licenseAdmin.chartData.sets.reportsagg ?? "")),    // Chart.js extends this array, so we need to deep copy
            title: s.licenseAdmin.chartSumReports + " " + timeConverter(state.licenseAdmin.chartData.from, null, s.common.dateFormat) + " - " + timeConverter(state.licenseAdmin.chartData.to, null, s.common.dateFormat)
        },
        creditsagg: {
            value: "creditsagg",
            label: s.licenseAdmin.chartSumCredits,
            datasets: JSON.parse(JSON.stringify(state.licenseAdmin.chartData.sets.creditsagg ?? "")), // Chart.js extends this array, so we need to deep copy
            title: s.licenseAdmin.chartSumCredits + " (" + timeConverter(state.licenseAdmin.chartData.from, null, s.common.dateFormat) + " - " + timeConverter(state.licenseAdmin.chartData.to, null, s.common.dateFormat) + ")"
        }
    }
    const chartModesArray = Object.entries(chartModes);

    // reload users if filter settings have changed
    useEffect(() => {
        loadLicenses();
    }, [state.licenseAdmin.usersFilter, state.licenseAdmin.usersPagination, state.user.settings.pagesize, state.licenseUsersChangeNo]);

    // update local slider value with state value on change
    useEffect(() => {
        if (license.perUserQuota)
            setPerUserQuota(license.perUserQuota);
    }, [license.perUserQuota]);



    function loadLicenses() {
        setLoading(true);
        Service.getLicenseUsers({ filters: state.licenseAdmin.usersFilter, ...state.licenseAdmin.usersPagination, pagesize: state.user.settings.pagesize }).then(res => {
            setLoading(false);
            if (res && res.success && res.data) {
                dispatch(actions.setAdminLicense({ users: res.data, userCount: res.total }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.message, s.notificationMessage) }));
            }
        });
        Service.getLicenseAdmin().then(res => {
            if (res && res.success && res.data) {
                dispatch(actions.setAdminLicense({ currentLicense: res.data.currentLicense, subLicenses: res.data.subLicenses, phases: res.data.phases, nextInvoiceData: res.data.nextInvoiceData }));
                setWelcomeMessage(res.data.currentLicense.welcomeMessage);
                setPerUserQuota(res.data.currentLicense.perUserQuota);
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.message, s.notificationMessage) }));
            }
        });
        Service.getLicenseUsageChartData({ filters: state.licenseAdmin.usersFilter }).then(res => {
            if (res && res.success && res.data) {
                dispatch(actions.setAdminLicense({ chartData: res.data }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.message, s.notificationMessage) }));
            }
        });

    }

    const onRemoveLicense = (row) => {
        Service.removeUserLicense(row.Id).then((res) => {
            if (res && res.success) {
                dispatch(actions.notifyLicenseUsersChanged());
                dispatch(actions.openToast({ message: resolveNotificationMessage(s.notificationMessage.licenseRemoved, s.notificationMessage) }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const columns = useMemo(() => [
        {
            Header: ({ getToggleAllRowsSelectedProps }) => <Checkbox size="small" {...getToggleAllRowsSelectedProps()} />,
            accessor: "active",
            Cell: ({ row }) => <Checkbox size="small" onClick={(e) => e.stopPropagation()} {...row.getToggleRowSelectedProps()} />,
            headerClassName: classes.table_status_header,
            cellClassName: classes.table_status_header,
            disableSortBy: true,
        },
        {
            Header: s.licenseAdmin.license,
            accessor: "license",
            headerClassName: classes.table_license_header,
            cellClassName: classes.table_license_header,
            Cell: ({ cell: { value, row } }) => <div style={{ color: (row.original.status == "User") ? "black" : "gray" }}>{value}</div>,
        },
        {
            Header: s.licenseAdmin.userName,
            accessor: "mail",
            headerClassName: classes.table_mail_header,
            cellClassName: classes.table_mail_header,
            Cell: ({ cell: { value, row } }) => <div style={{ color: (row.original.status == "User") ? "black" : "gray" }}>{value}</div>,
        },
        {
            Header: s.credits,
            accessor: "credits",
            Cell: ({ cell: { value, row } }) => <div>{value != 0 ? value : "-"}</div>,
            headerClassName: classes.table_credits_header,
            cellClassName: classes.table_credits_header,
        },
        {
            Header: s.actions,
            accessor: s.actions,
            headerClassName: classes.table_action_header,
            cellClassName: classes.table_action_header,
            disableSortBy: true,
            Cell: ({ cell: { value, row } }) =>
                <div>
                    <IconButton data-for="globalTT" data-tip={s.licenseAdmin.removeUser} size="small"
                        onClick={(e) => onRemoveLicense(row.original)}>
                        <RemoveCircleOutlineIcon />
                    </IconButton>
                </div>,
        },
    ], []
    );

    const onActiveRowHandle = useCallback((rowDetails) => {
        setActiveId(rowDetails && rowDetails.Id || null);
    });

    const rowSelectedHandle = useCallback((rowDetails) => {
        setSelectedIds(rowDetails || []);
    });

    const handleSort = useCallback((sortBy) => {
        const sort = {
            pageno: 1,
            sortby: sortBy && sortBy[0] ? sortBy[0].id : 'mail',
            sortdir: sortBy && sortBy[0] && sortBy[0].desc ? 'desc' : 'asc'
        };
        dispatch(actions.setAdminLicense({ usersPagination: sort }));
    });

    const onPayButtonClicked = (id) => {
        dispatch(actions.setCheckoutDetails({ paymentStep: 4, invoiceId: id }));
        dispatch(actions.setUI({ paymentDialogVisible: true }));
    }


    const onRenewButtonClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.licenseAdmin.renewLicenseTitle,
            text: s.licenseAdmin.renewLicenseText,
            okLabel: s.licenseAdmin.renewlicenseOkButton,
            cancelLabel: s.licenseAdmin.renewLicenseCancelLabel,
            onConfirm: () => onConfirmRenewLicense(),
            hint: s.licenseAdmin.renewLicenseHint.format(
                termsLabel,
                license.contractCredits,
                formatMoney(state.licenseAdmin.nextInvoiceData.Betrag),
                state.licenseAdmin.nextInvoiceData.Currency
            ),
            waitingMessage: s.licenseAdmin.renewLicenseWaiting
        }));
    }

    const onConfirmRenewLicense = () => {
        Service.extendLicense().then((res) => {
            dispatch(actions.closeConfirmationDialog());
            if (res && res.success) {
                dispatch(actions.openToast({ message: s.licenseAdmin.renewedSuccessfully }));
                loadLicenses();
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const onChangePage = (page, pageLength) => {
        if (page != state.licenseAdmin.usersPagination.pageno)
            dispatch(actions.setAdminLicense({ usersPagination: { ...state.licenseAdmin.usersPagination, pageno: page } }));
        if (pageLength != state.user.settings.pagesize)
            dispatch(actions.setUserSettings({ pagesize: pageLength }));
    }

    const onSearchChanged = useCallback((s) => {
        dispatch(actions.setAdminLicense({ usersFilter: { ...state.licenseAdmin.usersFilter, search: s } }));
    });

    const onDownloadInvoiceClicked = (id) => {
        const url = config.backendUrl + "export/?rid=" + id;
        clickDownloadLink(url, "blank");
    }

    const onTerminateSubscriptionClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.licenseAdmin.terminateSubscription,
            text: s.licenseAdmin.terminateSubscriptionText,
            hint: s.licenseAdmin.termateSubscriptionHint.format(timeConverter(license.validTo, true, s.common.dateFormat)),
            onConfirm: () => { onTerminateSubscriptionConfirmed() },
            okLabel: s.licenseAdmin.terminateLabel,
            cancelLabel: s.common.cancel
        }));
    }

    const onTerminateSubscriptionConfirmed = () => {
        Service.updateLicense(license.code, { autoExtend: false }).then((res) => {
            if (res && res.success) {
                dispatch(actions.openToast({ message: s.licenseAdmin.licenseTerminated }));
                loadLicenses();
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const onEnableRenewalClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.licenseAdmin.enableRenewal,
            text: s.licenseAdmin.enableRenewalText,
            hint: s.licenseAdmin.extensionHint.format(
                timeConverter(license.validTo, null, s.common.dateFormat),
                termsLabel,
                formatMoney(state.licenseAdmin.nextInvoiceData.Betrag),
                state.licenseAdmin.nextInvoiceData.Currency),
            onConfirm: () => { onEnableRenewalConfirmed() },
        }));
    }

    const onEnableRenewalConfirmed = () => {
        Service.updateLicense(license.code, { autoExtend: true }).then((res) => {
            if (res && res.success) {
                dispatch(actions.openToast({ message: s.licenseAdmin.licenseActivated }));
                loadLicenses();
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const handleChange = (newValue, key) => {
        Service.updateLicense(license.code, { [key]: newValue }).then((res) => {
            if (res && res.success) {
                dispatch(actions.setAdminLicense({ currentLicense: { ...license, [key]: newValue } }));
                if (key == "forceUserSettings") {   // reload license data to make sure the "settings enforced" hint shoows up in  the settings dialog
                    Service.getUserInfo().then(getInfoRes => {
                        dispatch(actions.setUserDetails(getInfoRes.data));
                    });
                }
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const handleToggleRestrictMailFormat = (checked) => {
        if (checked) {
            dispatch(actions.openPasteDialog({
                title: s.licenseAdmin.mailFormatTitle,
                description: s.licenseAdmin.mailFormatDescription,
                placeholder: s.licenseAdmin.mailFormatPlaceholder,
                okLabel: s.ok,
                cancelLabel: s.cancel,
                onConfirm: handleMailFormatChange
            }));
        } else {
            handleChange(null, "mailFormat");
        }

    }

    const handleMailFormatChange = (payload, newValue) => {
        Service.updateLicense(license.code, { mailFormat: newValue }).then((res) => {
            if (res && res.success) {
                dispatch(actions.setAdminLicense({ currentLicense: { ...license, mailFormat: newValue } }));
                dispatch(actions.closePasteDialog());
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const remainingLabel = (remaining, total) => (remaining == -1) ? s.dashboard.unlimited : (total) ? s.licenseAdmin.remainingOfTotal.format(remaining, total) : remaining;

    const subLicenseColumns = useMemo(() => [
        { header: s.licenseAdmin.license, accessor: "code", headerStyles: { width: 120 } },
        { header: s.licenseAdmin.subLicenseName, accessor: "comment", },
        { header: s.licenseAdmin.administrator, accessor: "adminUserMail", },
        { header: s.licenseAdmin.subLicenseSharing, headerStyles: { width: 100 }, value: (row) => (row.shareTexts == "N") ? s.licenseAdmin.none : (row.shareTexts == "Y") ? s.licenseAdmin.matches : s.licenseAdmin.fulltext },
        { header: s.credits, headerStyles: { width: 100, textAlign: "center" }, bodyStyles: { textAlign: "right" }, value: (row) => remainingLabel(row.remaining, row.credits) },
    ], [state.lang]);

    const subLicenseTable = useMemo(() =>
        <SimpleTable data={state.licenseAdmin.subLicenses} columns={subLicenseColumns} showHeader emptyMessage={<IconLabel type="info" label={s.licenseAdmin.noSubLicences} />} />
        , [state.licenseAdmin.subLicenses, state.lang]);

    const licenePhasesColumns = useMemo(() => [
        { header: s.licenseAdmin.phase, headerStyles: { width: 60 }, accessor: 'phase' },
        { header: s.licenseAdmin.servicePeriod, value: (row) => timeConverter(row.validFrom, true, s.common.dateFormat) + " - " + timeConverter(row.validTo, true, s.common.dateFormat) },
        { header: s.licenseAdmin.invoice, headerStyles: { width: 120 }, value: (row) => <Link style={{ color: (row.payDate) ? "green" : "red", cursor: "pointer" }} onClick={(e) => onDownloadInvoiceClicked(row.invoiceId)}>{row.invoiceId}</Link> },
        { header: s.credits, headerStyles: { width: 80, textAlign: "right" }, bodyStyles: { textAlign: "right" }, value: (row) => row.targetCredits ? s.dashboard.unlimited : row.contractCredits },
    ], [state.lang]);

    const licenePhasesTable = useMemo(() =>
        <SimpleTable data={state.licenseAdmin.phases.slice(0, 3)} columns={licenePhasesColumns} showHeader emptyMessage={<IconLabel type="info" label={s.licenseAdmin.noPhases} />} />
        , [state.licenseAdmin.phases, state.lang]);

    const onWelcomeMessageChanged = (value) => {
        setWelcomeMessage(value);
    }

    const onEditMessageClicked = () => {
        if (editMarkdown) {
            Service.updateLicense(license.code, { welcomeMessage: welcomeMessage }).then((res) => {
                if (res && res.success) {
                    setEditMarkdown(false);
                    loadLicenses();
                } else {
                    dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
                }
            });
        } else {
            setEditMarkdown(true);
        }
    }

    const onAddUsersButtonClicked = () => {
        dispatch(actions.openPasteDialog({
            useArea: true,
            title: s.licenseAdmin.pasteUsersTitle,
            placeholder: s.licenseAdmin.pasteFormat,
            okLabel: s.common.preview,
            cancelLabel: s.common.cancel,
            onConfirm: onPreviewImport,
            waitingMessage: s.licenseAdmin.addingUsers,
            option: { label: s.licenseAdmin.synchronizeUsers },
            hint: s.licenseAdmin.addUsersHint
        }));
    }

    const onPreviewImport = (payload, text, syncUsers) => {
        if (!text) {
            dispatch(actions.closePasteDialog());
            return;
        }

        Service.addUsers(text, syncUsers, true).then((res) => {    // true: preview only
            dispatch(actions.closePasteDialog());
            if (res.success) {
                dispatch(actions.openLogDialog({
                    title: (res.data.RemovedUsers > 0) ? s.licenseAdmin.previewSyncUsers : s.licenseAdmin.previewAddUsers,
                    markdown: res.message,
                    onConfirm: (res.data.RemovedUsers > 0) ? () => onConfirmSyncUsers(text, res.data.RemovedUsers) :
                        (res.data.CheckedUsers > 0) ? () => { onPerformImport(text, syncUsers); } : null,
                    okLabel: (res.data.RemovedUsers > 0) ? s.licenseAdmin.startUsersync : s.licenseAdmin.startImport,
                    cancelLabel: s.common.cancel,
                    waitingMessage: s.licenseAdmin.addingUsers,
                }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const onConfirmSyncUsers = (text, removeUserCount) => {
        dispatch(actions.openConfirmationDialog({
            title: s.licenseAdmin.userSyncWarningTitle.format(removeUserCount),
            text: s.licenseAdmin.userSyncWarningText.format(removeUserCount),
            hint: s.licenseAdmin.userSyncWarningHint,
            onConfirm: () => { onPerformImport(text, true); },
            okLabel: s.licenseAdmin.startUsersync,
            cancelLabel: s.common.cancel
        }));
    }

    const onPerformImport = (text, syncUsers) => {
        if (!text) {
            dispatch(actions.closePasteDialog());
            return;
        }

        Service.addUsers(text, syncUsers, false).then((res) => {
            dispatch(actions.closeLogDialog());
            if (res.success) {
                dispatch(actions.notifyLicenseUsersChanged());
                dispatch(actions.openConfirmationDialog({
                    title: s.licenseAdmin.fileImported,
                    text: s.licenseAdmin.userAddedCount.format(res.data.AddedUsers + res.data.InvitedUsers, res.data.RemovedUsers, res.data.SkippedUsers),
                    okLabel: s.common.ok
                }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }


    const onResetRUButtonClicked = () => {
        dispatch(actions.openConfirmationDialog({
            title: s.licenseAdmin.resetRUTitle,
            text: s.licenseAdmin.resetReadUnderstoodTip,
            onConfirm: () => { onResetRUConfirmed() },
            okLabel: s.common.ok,
            cancelLabel: s.common.cancel
        }));
    }
    const onResetRUConfirmed = () => {
        Service.updateLicense(license.code, { resetRU: true }).then((res) => {
            if (res && res.success) {
                dispatch(actions.openToast({ type: "success", message: s.licenseAdmin.resetRUSuccess }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const openExportFormatPopup = (event) => {
        setExportFormatPopup(event.currentTarget);
    }
    const closeExportFormatPopup = () => {
        setExportFormatPopup(null);
    }

    const downloadUsageStats = (type) => {
        closeExportFormatPopup();
        Service.getLicenseUsageStatistics(state.licenseAdmin.usersFilter, type, "csv").then((res) => {
            if (res && res.success) {
                const url = config.backendUrl + "export/?csv";
                clickDownloadLink(url);
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }

    const onDownloadIntro = () => {
        Service.createLicenseIntro().then((res) => {
            if (res && res.success) {
                const url = config.backendUrl + "export/?exp";
                clickDownloadLink(url);
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
            }
        });
    }


    return (
        <div className={classes.root}>
            <TitleBar title={s.licenseAdmin.title} type="LicenseAdmin" onSearchChanged={onSearchChanged} />
            <Popover
                anchorEl={exportFormatPopup}
                keepMounted
                open={!!exportFormatPopup}
                onClose={closeExportFormatPopup}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                <Box mt={1} />
                <MenuItem onClick={() => downloadUsageStats("bymonth")}>{s.licenseAdmin.groupByMonth}</MenuItem>
                <MenuItem onClick={() => downloadUsageStats("bylicense")}>{s.licenseAdmin.groupByLicense}</MenuItem>
                <MenuItem onClick={() => downloadUsageStats("users")}>{s.licenseAdmin.users}</MenuItem>
                <Box mt={1} />
            </Popover>
            <div className={classes.bodyContainer}>
                <div className={classes.detailsPanel}>
                    {license.code && <>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <div>
                                <Typography>{s.licenseAdmin.yourLicense.format(license.code)}</Typography>
                                <Typography>{s.licenseAdmin.licensee.format(license.licensee)}</Typography>
                                {license.isChild && <Typography>{s.licenseAdmin.subLicenseTitle.format(license.comment)}</Typography>}
                                <Typography>{s.licenseAdmin.licenseType.format(license.type == 1 ? s.dashboard.educationalLicense : s.dashboard.volumeLicense + " " + license.contractCredits + " " + s.credits + " / " + termsLabel)}</Typography>
                                <Typography>{s.licenseAdmin.remainingCredits.format(remainingLabel(license.remaining, license.credits))}</Typography>
                                {!license.isChild && <Typography>{s.licenseAdmin.servicePeriodLabel.format(timeConverter(license.validFrom, true, s.common.dateFormat), timeConverter(license.validTo, true, s.common.dateFormat))}</Typography>}
                            </div>
                            <div style={{ flex: "0 1 250px", display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end" }}>
                                {state.user.logo && <img style={{ marginTop: 8, height: "max-content", width: "max-content" }} className={classes.logo} src={config.backendUrl + state.user.logo} alt="logo" />}
                                <Button variant="outlined" onClick={onDownloadIntro}>{s.licenseAdmin.downloadIntro}</Button>
                            </div>
                        </div>

                        <DividerLabel label={s.licenseAdmin.dividerUserRegistration} />
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                            <Typography style={{ flex: "1 1 auto" }}>{s.licenseAdmin.uploadListLabel}</Typography>
                            <Button style={{ flex: "0 1 250px" }} variant="outlined" onClick={onAddUsersButtonClicked}>{s.licenseAdmin.addUsers}</Button>
                        </div>

                        <DividerLabel label={s.licenseAdmin.userRegistration} />
                        {!license.updateSettingsAllowed && <IconLabel mb={8}> {s.licenseAdmin.settingsInherited}</IconLabel>}

                        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                            <FormControlLabel className={classes.switch}
                                control={<Switch disabled={!license.updateSettingsAllowed} checked={license.requireAccountActivation} onChange={(e) => handleChange(e.target.checked, "requireAccountActivation")} />}
                                label={s.licenseAdmin.requireAccountActivation}
                            />
                            <FormControlLabel className={classes.switch}
                                control={<Switch disabled={!license.updateSettingsAllowed} checked={license.allowSelfRegistration} onChange={(e) => handleChange(e.target.checked, "allowSelfRegistration")} />}
                                label={s.licenseAdmin.allowSelfRegisitration}
                            />
                            <FormControlLabel className={classes.switch}
                                control={<Switch disabled={!license.updateSettingsAllowed} checked={license.requireAdminApproval} onChange={(e) => handleChange(e.target.checked, "requireAdminApproval")} />}
                                label={s.licenseAdmin.requireAdminApproval}
                            />
                            <div style={{ display: "flex", alignItems: "baseline" }}>
                                <FormControlLabel className={classes.switch}
                                    control={<Switch disabled={!license.updateSettingsAllowed} checked={license.mailFormat} onChange={(e) => handleToggleRestrictMailFormat(e.target.checked)} />}
                                    label={s.licenseAdmin.mailFormatTitle + (license.mailFormat ? ": " + license.mailFormat : "")}
                                />
                            </div>
                        </div>


                        <DividerLabel label={s.licenseAdmin.dividerTextSharing} />
                        {!license.updateSettingsAllowed && <IconLabel mb={16}> {s.licenseAdmin.settingsInherited}</IconLabel>}

                        <RadioGroup name="shareTexts" value={license.shareTexts} onChange={(e) => handleChange(e.target.value, "shareTexts")}>
                            <FormControlLabel value="F" control={<Radio disabled={!license.updateSettingsAllowed} checked={license.shareTexts == "F"} />} label={s.licenseAdmin.shareFullText} />
                            <FormControlLabel value="Y" control={<Radio disabled={!license.updateSettingsAllowed} checked={license.shareTexts == "Y"} />} label={s.licenseAdmin.shareFindings} />
                            <FormControlLabel value="N" control={<Radio disabled={!license.updateSettingsAllowed} checked={license.shareTexts == "N"} />} label={s.licenseAdmin.noSharing} />
                        </RadioGroup>

                        <DividerLabel label={s.licenseAdmin.userSettings} />
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}>
                            <FormControlLabel className={classes.switch}
                                control={<Switch disabled={!license.updateSettingsAllowed} checked={license.forceUserSettings} onChange={(e) => handleChange(e.target.checked, "forceUserSettings")} />}
                                label={s.licenseAdmin.forceUserSettings}
                            />
                            <FormControlLabel style={{ marginTop: 8 }} className={classes.slider}
                                control={<Slider value={perUserQuota} min={0} max={1000} step={50} valueLabelDisplay="auto" style={{ width: 150, marginLeft: 16, marginRight: 16 }}
                                    onChange={(e, value) => setPerUserQuota(value)}
                                    onChangeCommitted={(e, value) => handleChange(value, "perUserQuota")} />}
                                label={(perUserQuota > 0) ? s.licenseAdmin.perUserQuota.format(perUserQuota) : s.licenseAdmin.perUserQuotaUnlimited}
                                labelPlacement="end"
                            />
                        </div>


                    </>}
                </div>
                {state.ui.licenseView == "usage" &&
                    <Paper elevation={3} className={classes.paper}>
                        <LoadingScreen status={loading} />
                        <div className={classes.tableContainer}>
                            {(state.licenseAdmin.users.length > 0) ?
                                <Table
                                    data={state.licenseAdmin.users}
                                    onSelectedRowsChange={rowSelectedHandle}
                                    selectedIds={selectedIds}
                                    columns={columns}
                                    onActiveRowChanged={onActiveRowHandle}
                                    activeRowId={activeId}
                                    onSort={handleSort}
                                    onSortBy={state.licenseAdmin.usersPagination}

                                />
                                : (!loading && <IconLabel centered label={s.licenseAdmin.noUserActivities} spacing={32} />)}
                        </div>

                        <div className={classes.bottom}>

                            {selectedIds.length > 0 &&
                                <div style={{ display: "flex", alignItems: "center", marginRight: 16 }}>
                                    <Typography variant="body2">{s.licenseAdmin.selectedUsers.format(selectedIds.length)}</Typography>
                                </div>
                            }
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <IconButton data-for="globalTT" data-tip={s.licenseAdmin.downloadStats} size="small" onClick={openExportFormatPopup} >
                                    <Equalizer />
                                </IconButton>
                            </div>

                            <div className={classes.pagination}>
                                <Pagination
                                    page={state.licenseAdmin.usersPagination.pageno}
                                    pageLength={state.user.settings.pagesize}
                                    totalRecords={state.licenseAdmin.userCount || 0}
                                    onchange={onChangePage}
                                />
                            </div>
                        </div>
                    </Paper>

                }
                {state.ui.licenseView == "settings" &&

                    <Paper elevation={3} className={classes.paper}>
                        <div style={{ padding: 15, minHeight: 0, overflow: "auto" }}>
                            {!license.isChild && license.invoiceId && !license.paid &&
                                <div style={{ display: "flex", justifyItems: "center", justifyContent: "space-between" }}>
                                    <IconLabel type="warning" label={s.licenseAdmin.notPaid} />
                                    <div style={{ width: 220, margin: 4 }}>
                                        <Button onClick={() => onPayButtonClicked(license.invoiceId)} variant="contained" style={{ width: "100%", backgroundColor: '#BB0000', color: 'white' }}>{s.userDialog.payNow}</Button>
                                    </div>
                                </div>}

                            {!license.isChild && <>
                                <DividerLabel label={s.licenseAdmin.dividerRenewal} />
                                {license.valid && license.renewal && <>
                                    <IconLabel type="check" label={s.licenseAdmin.extensionHint.format(
                                        timeConverter(license.renewal, null, s.common.dateFormat),
                                        termsLabel,
                                        formatMoney(state.licenseAdmin.nextInvoiceData.Betrag),
                                        state.licenseAdmin.nextInvoiceData.Currency
                                    )} />
                                </>
                                }
                                {license.valid && !license.renewal && <IconLabel type="warning" label={s.licenseAdmin.noExtensionHint.format(timeConverter(license.validTo, null, s.common.dateFormat))} />}
                                {!license.valid && <Typography color="error">{s.licenseAdmin.licenseNotValid}</Typography>}
                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    {license.type == 2 &&
                                        license.invoiceId &&
                                        license.valid && <div data-for={(!license.paid || noRenewPeriod) && "globalTT"} data-tip={s.licenseAdmin.noRenewTip} style={{ width: 220, margin: 4 }}><Button disabled={!license.paid || noRenewPeriod} style={{ width: "100%" }} variant="outlined" onClick={onRenewButtonClicked}>{s.licenseAdmin.renewEarly}</Button></div>}
                                    {license.renewal && license.invoiceId && license.valid &&
                                        <div style={{ width: 220, margin: 4 }}><Button style={{ width: "100%", color: "red" }} variant="outlined" onClick={onTerminateSubscriptionClicked}>{s.licenseAdmin.terminateSubscription}</Button></div>}
                                    {!license.renewal && license.invoiceId && license.valid &&
                                        <div style={{ width: 220, margin: 4 }}><Button variant="contained" color="primary" style={{ width: "100%" }} onClick={onEnableRenewalClicked}>{s.licenseAdmin.enableRenewal}</Button></div>}
                                </div>

                                <DividerLabel label={s.licenseAdmin.dividerPhases} />
                                {licenePhasesTable}

                                <DividerLabel label={s.licenseAdmin.dividerChildLicenses} />
                                <FormControlLabel className={classes.switch}
                                    control={<Switch checked={license.allowSubLicenseOverwrite} onChange={(e) => handleChange(e.target.checked, "allowSubLicenseOverwrite")} />}
                                    label={s.licenseAdmin.allowSubLicenseOverwrite}
                                />
                                {subLicenseTable}

                            </>}

                        </div>
                    </Paper>
                }
                {state.ui.licenseView == "message" &&

                    <Paper elevation={3} className={classes.paper}>
                        <LoadingScreen status={loading} />
                        <div style={{ padding: 15, display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                            <IconLabel label={s.licenseAdmin.welcomeTip} />
                            {!loading && editMarkdown &&
                                <textarea style={{ flex: "1 1 0", marginTop: 15, marginBottom: 15 }} className={classes.textarea}
                                    value={welcomeMessage}
                                    onChange={(e) => { onWelcomeMessageChanged(e.target.value) }}
                                    placeholder={s.licenseAdmin.welcomePlaceholder}>
                                </textarea>}
                            {!loading && !editMarkdown &&
                                <div style={{ flex: "1 1 0", overflow: "auto", marginTop: 15, marginBottom: 15 }}>
                                    <ReactMarkdown>{welcomeMessage ? welcomeMessage : s.licenseAdmin.welcomeMessagePlaceholder}</ReactMarkdown>
                                </div>
                            }
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button disabled={loading} style={{ marginRight: 8 }} variant="outlined" onClick={onEditMessageClicked}>{editMarkdown ? s.licenseAdmin.saveMessage : s.licenseAdmin.editMessage}</Button>
                                <Button disabled={loading || editMarkdown || !license.welcomeMessage} data-for="globalTT" data-tip={s.licenseAdmin.resetReadUnderstoodTip} variant="outlined" onClick={onResetRUButtonClicked}>{s.licenseAdmin.resetReadUnderstood}</Button>
                            </div>

                        </div>
                    </Paper>
                }
                {state.ui.licenseView == "statistics" &&

                    <Paper elevation={3} className={classes.paper}>
                        <LoadingScreen status={loading} />
                        <div style={{ padding: 15, display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                <ToggleButtonGroup
                                    value={chartValue}
                                    exclusive
                                    onChange={(e, v) => { v && setChartValue(v) }}
                                    size="small"

                                >
                                    {
                                        chartModesArray.map(([key, value]) => {
                                            return <ToggleButton value={value.value}>{value.label}</ToggleButton>
                                        })
                                    }
                                </ToggleButtonGroup>
                            </div>


                            {!loading &&
                                <DashboardChart id="licensestats" height="100%" 
                                    type = "bar"
                                    labels={state.licenseAdmin.chartData.sets.labels}
                                    datasets={chartModes[chartValue].datasets}
                                    title={chartModes[chartValue].title}
                                />
                            }
                        </div>
                    </Paper>
                }

            </div>
        </div >
    );
}